import {
  assistantScopes,
  getAssistants,
  listTrips,
  makeKey,
  Pick,
  revokeAssistant,
  type Assistants,
  type AssistantScope,
  type Granted,
  type TripSummary,
} from '@trippath/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayRange } from '../../lib/countryFacts';

const locale = (language: string) => (language === 'et' ? 'et-EE' : 'en-GB');

/** `18 Oct` from an instant. */
function dayOf(at: string, language: string): string {
  return new Intl.DateTimeFormat(locale(language), { day: 'numeric', month: 'short' }).format(
    new Date(at),
  );
}

/** `14:06` from an instant. */
function timeOf(at: string, language: string): string {
  return new Intl.DateTimeFormat(locale(language), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(at));
}

/** Copies `text`, and says so a moment. */
export function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="button button-small"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
        });
      }}
    >
      {t(done ? 'assistant.copied' : 'assistant.copy')}
    </button>
  );
}

/** The scopes ticked: one box each, said with what it gives. */
export function ScopeChecks({
  chosen,
  onChange,
}: {
  chosen: AssistantScope[];
  onChange: (chosen: AssistantScope[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="assistant-scopes-pick">
      {assistantScopes.map((scope) => (
        <label key={scope} className="check assistant-check">
          <input
            type="checkbox"
            checked={chosen.includes(scope)}
            onChange={(event) => {
              onChange(
                assistantScopes.filter((one) =>
                  one === scope ? event.target.checked : chosen.includes(one),
                ),
              );
            }}
          />
          <span>
            {t(`assistant.give.${scope}`)}
            <small className="quiet">{t(`assistant.give.${scope}More`)}</small>
          </span>
        </label>
      ))}
    </div>
  );
}

/** Every trip, or only one: the choice of trips a permission is for. */
export function TripsPick({
  trips,
  chosen,
  onChange,
}: {
  trips: TripSummary[];
  chosen: string;
  onChange: (chosen: string) => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Pick
      label={t('assistant.trips')}
      searchable={false}
      options={[
        { value: '', label: t('assistant.allTrips') },
        ...trips.map((trip) => ({
          value: trip.id,
          label: t('assistant.onlyTrip', {
            name: trip.name,
            days: dayRange(trip.start, trip.end, i18n.language),
          }),
        })),
      ]}
      value={chosen}
      onChange={onChange}
    />
  );
}

/** One assistant or key: since when, how often and when last used, what it may do; taken back. */
function GrantedRow({
  granted,
  trips,
  onRevoke,
}: {
  granted: Granted;
  trips: TripSummary[];
  onRevoke: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const last = granted.lastUsedAt;
  const when =
    last === null
      ? null
      : new Date(last).toDateString() === new Date().toDateString()
        ? t('assistant.today', { time: timeOf(last, lang) })
        : dayOf(last, lang);
  const missing = assistantScopes.filter((scope) => !granted.scopes.includes(scope));
  const names = (granted.trips ?? []).map(
    (id) => trips.find((trip) => trip.id === id)?.name ?? id,
  );
  return (
    <li className="assistant-client">
      <div>
        <b>{granted.name}</b>
        <div className="quiet">
          {[
            t('assistant.since', { day: dayOf(granted.createdAt, lang) }),
            ...(when === null ? [] : [t('assistant.lastUsed', { when })]),
            ...(granted.uses > 0 ? [t('assistant.uses', { count: granted.uses })] : []),
          ].join(' · ')}
        </div>
      </div>
      <button type="button" className="button button-small button-danger" onClick={onRevoke}>
        {t(granted.assistant ? 'assistant.revoke' : 'assistant.revokeKey')}
      </button>
      <div className="assistant-scopes">
        {granted.scopes.map((scope) => (
          <span key={scope} className="assistant-tag assistant-tag-on">
            {t(`assistant.scope.${scope}`)}
          </span>
        ))}
        {granted.scopes.length === 1 ? (
          <span className="assistant-tag">{t('assistant.allElse')}</span>
        ) : (
          missing.map((scope) => (
            <span key={scope} className="assistant-tag">
              {t(`assistant.scopeNo.${scope}`)}
            </span>
          ))
        )}
        {names.length > 0 && (
          <span className="assistant-tag assistant-tag-trips">
            {t('assistant.tripsOf', { trips: names.join(', ') })}
          </span>
        )}
      </div>
    </li>
  );
}

/** A personal key made: named, its scopes and trips chosen, its token shown once. */
function NewKey({ trips, onMade }: { trips: TripSummary[]; onMade: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<AssistantScope[]>(['trips']);
  const [trip, setTrip] = useState('');
  const [state, setState] = useState<
    { kind: 'idle' } | { kind: 'failed' } | { kind: 'made'; token: string }
  >({ kind: 'idle' });
  return (
    <form
      className="assistant-new-key"
      onSubmit={(event) => {
        event.preventDefault();
        makeKey(name, scopes, trip === '' ? null : [trip]).then(
          (token) => {
            setState({ kind: 'made', token });
            setName('');
            onMade();
          },
          () => {
            setState({ kind: 'failed' });
          },
        );
      }}
    >
      <label className="field">
        <span className="field-label">{t('assistant.keyName')}</span>
        <input
          className="text-input"
          type="text"
          value={name}
          placeholder={t('assistant.keyNamePlaceholder')}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </label>
      <ScopeChecks chosen={scopes} onChange={setScopes} />
      <TripsPick trips={trips} chosen={trip} onChange={setTrip} />
      {state.kind === 'failed' && (
        <p className="form-failed" role="alert">
          {t('assistant.keyFailed')}
        </p>
      )}
      {state.kind === 'made' && (
        <div className="assistant-made" role="status">
          <span>{t('assistant.keyMade')}</span>
          <div className="assistant-url">
            <code>{state.token}</code>
            <CopyButton text={state.token} />
          </div>
        </div>
      )}
      <div className="actions">
        <button type="submit" className="button button-primary">
          {t('assistant.makeKey')}
        </button>
      </div>
    </form>
  );
}

/** The example conversation: how an assistant reads, shows a change, and makes it on a yes. */
function Example() {
  const { t } = useTranslation();
  return (
    <section className="panel assistant-example" aria-labelledby="assistant-example">
      <div className="panel-head">
        <h2 id="assistant-example">{t('assistant.example.title')}</h2>
      </div>
      <div className="assistant-chat">
        <p className="assistant-msg assistant-me">{t('assistant.example.ask')}</p>
        <p className="assistant-tool">{t('assistant.example.read')}</p>
        <p className="assistant-tool">{t('assistant.example.preview')}</p>
        <p className="assistant-msg assistant-ai">{t('assistant.example.says')}</p>
        <div className="assistant-change">
          <b>{t('assistant.example.shown')}</b>
          <div className="assistant-delta">
            <span className="assistant-bad">{t('assistant.example.more')}</span>
            <span>{t('assistant.example.budget')}</span>
            <span className="assistant-good">{t('assistant.example.visa')}</span>
          </div>
          <span className="quiet">{t('assistant.example.unused')}</span>
        </div>
        <p className="assistant-msg assistant-me">{t('assistant.example.yes')}</p>
        <p className="assistant-tool">{t('assistant.example.apply')}</p>
        <p className="assistant-msg assistant-ai">{t('assistant.example.done')}</p>
        <ol className="assistant-notes quiet">
          <li>{t('assistant.example.twoSteps')}</li>
          <li>{t('assistant.example.history')}</li>
          <li>{t('assistant.example.limits')}</li>
        </ol>
      </div>
    </section>
  );
}

/** One's own AI assistant (the `Assistant` mockup): where it connects, what has leave, keys. */
export function Assistant() {
  const { t } = useTranslation();
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; assistants: Assistants }
  >({ kind: 'loading' });
  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [revokeFailed, setRevokeFailed] = useState(false);
  const load = useCallback(() => {
    getAssistants().then(
      (assistants) => {
        setState({ kind: 'ready', assistants });
      },
      () => {
        setState({ kind: 'failed' });
      },
    );
  }, []);
  useEffect(() => {
    load();
    listTrips().then(setTrips, () => {
      setTrips([]);
    });
  }, [load]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind === 'failed') {
    return (
      <p className="page form-failed" role="alert">
        {t('assistant.failed')}
      </p>
    );
  }
  const { mcp, granted } = state.assistants;
  const assistants = granted.filter((one) => one.assistant);
  const keys = granted.filter((one) => !one.assistant);
  const row = (one: Granted) => (
    <GrantedRow
      key={one.id}
      granted={one}
      trips={trips}
      onRevoke={() => {
        setRevokeFailed(false);
        revokeAssistant(one.id).then(load, () => {
          setRevokeFailed(true);
        });
      }}
    />
  );
  return (
    <div className="page assistant-page">
      <header>
        <h1>{t('assistant.title')}</h1>
        <p className="quiet assistant-lead">{t('assistant.lead')}</p>
      </header>
      {revokeFailed && (
        <p className="form-failed" role="alert">
          {t('assistant.revokeFailed')}
        </p>
      )}
      <div className="assistant-layout">
        <div className="assistant-column">
          <section className="panel" aria-labelledby="assistant-connect">
            <div className="panel-head">
              <h2 id="assistant-connect">{t('assistant.connect')}</h2>
            </div>
            <div className="assistant-body">
              <span className="quiet">{t('assistant.connectHow')}</span>
              <div className="assistant-url">
                <code>{mcp}</code>
                <CopyButton text={mcp} />
              </div>
            </div>
          </section>
          <section className="panel" aria-labelledby="assistant-connected">
            <div className="panel-head">
              <h2 id="assistant-connected">
                {t('assistant.connected', { count: assistants.length })}
              </h2>
            </div>
            {assistants.length === 0 ? (
              <p className="assistant-body quiet">{t('assistant.none')}</p>
            ) : (
              <ul className="assistant-list">{assistants.map(row)}</ul>
            )}
          </section>
          <section className="panel" aria-labelledby="assistant-keys">
            <div className="panel-head">
              <h2 id="assistant-keys">{t('assistant.keys', { count: keys.length })}</h2>
            </div>
            {keys.length > 0 && <ul className="assistant-list">{keys.map(row)}</ul>}
            <div className="assistant-body">
              <span className="quiet">
                {keys.length === 0 && `${t('assistant.noKeys')} `}
                {t('assistant.keysHow')}
              </span>
              <NewKey trips={trips} onMade={load} />
            </div>
          </section>
        </div>
        <Example />
      </div>
    </div>
  );
}
